import React, { useEffect, useRef, useState } from 'react';

export function CountUp({ value, suffix = '', duration = 2000, decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const rafRef = useRef(null);
  const fromRef = useRef(0);

  // Observe intersection once
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Animate whenever value changes AND the element is in view
  useEffect(() => {
    if (!inView) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    fromRef.current = display;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(next);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inView, duration]);

  const formatted = decimals > 0 ? display.toFixed(decimals) : Math.floor(display).toLocaleString('fr-FR');
  return <span ref={ref}>{formatted}{suffix}</span>;
}

export function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect user preference for reduced motion
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(true);
      return undefined;
    }

    const node = ref.current;
    if (!node) return undefined;

    // Fallback: ensure content becomes visible even if IntersectionObserver never fires
    // (edge cases: fast scrolling on mobile, iOS Safari quirks, old browsers)
    const fallbackTimer = setTimeout(() => setVisible(true), 1500 + delay);

    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          clearTimeout(fallbackTimer);
        }
      },
      { threshold: 0.05, rootMargin: '0px 0px -5% 0px' }
    );
    obs.observe(node);

    return () => {
      clearTimeout(fallbackTimer);
      obs.disconnect();
    };
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? 'visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export function WaveDivider({ color = '#ffffff', flip = false }) {
  return (
    <div className={`absolute left-0 right-0 ${flip ? 'top-0' : 'bottom-0'} overflow-hidden leading-none pointer-events-none`} style={{ transform: flip ? 'rotate(180deg)' : 'none' }}>
      <svg viewBox="0 0 1440 90" className="w-full h-16 md:h-24" preserveAspectRatio="none">
        <path fill={color} d="M0,32L60,42.7C120,53,240,75,360,69.3C480,64,600,32,720,26.7C840,21,960,43,1080,48C1200,53,1320,43,1380,37.3L1440,32L1440,90L0,90Z" />
      </svg>
    </div>
  );
}
